import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Loading,
  useApiClient,
} from '@startupquickstart/frontend';
import { useQuery } from '@tanstack/react-query';
import { Languages } from 'lucide-react';
import { useEffect, useState } from 'react';

interface Language {
  id: string;
  name: string;
  code: string;
  isDefault: boolean;
}

interface GenerateTranslationsDialogProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  description?: string;
  onGenerate: (selectedLanguageIds: string[]) => void;
  isGenerating: boolean;
}

/**
 * Shared dialog component for generating translations to other languages.
 * Used by both attractions and themes.
 */
export function GenerateTranslationsDialog({
  open,
  onClose,
  title = 'Generate Translations',
  description = 'Select languages to translate to. Existing translations will be skipped.',
  onGenerate,
  isGenerating,
}: GenerateTranslationsDialogProps) {
  const [selectedLanguageIds, setSelectedLanguageIds] = useState<string[]>([]);
  const apiClient = useApiClient();

  // Fetch all available languages
  const { data: languagesResponse, isLoading } = useQuery({
    queryKey: ['Language'],
    queryFn: async () => {
      return apiClient.get<{ data: Language[] }>('/languages?limit=100');
    },
    enabled: open,
  });

  const languages = languagesResponse?.data || [];

  // Reset selection when dialog opens
  useEffect(() => {
    if (open) {
      setSelectedLanguageIds([]);
    }
  }, [open]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (selectedLanguageIds.length === 0) {
      return;
    }

    onGenerate(selectedLanguageIds);
  };

  const toggleLanguage = (languageId: string) => {
    setSelectedLanguageIds(prev =>
      prev.includes(languageId) ? prev.filter(id => id !== languageId) : [...prev, languageId],
    );
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px]">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Languages className="h-5 w-5" />
              {title}
            </DialogTitle>
            <DialogDescription>{description}</DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 py-4">
            {isLoading && <Loading text="Loading languages..." size="sm" />}

            {!isLoading && languages.length === 0 && (
              <div className="text-center text-sm text-muted-foreground">
                No languages available
              </div>
            )}

            {!isLoading && languages.length > 0 && (
              <div className="grid grid-cols-2 gap-2">
                {languages.map(language => (
                  <label
                    key={language.id}
                    className="flex items-center space-x-2 rounded-md border p-3 hover:bg-accent cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={selectedLanguageIds.includes(language.id)}
                      onChange={() => toggleLanguage(language.id)}
                      className="h-4 w-4"
                    />
                    <div className="flex-1">
                      <div className="font-medium">{language.name}</div>
                      <div className="text-xs text-muted-foreground">{language.code}</div>
                    </div>
                  </label>
                ))}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={isGenerating}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={selectedLanguageIds.length === 0}
              loading={isGenerating}
            >
              Generate Translations
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
